import { useCallback, useEffect, useRef } from 'react';

interface SwipeCarouselOptions {
  onSwipe?: (direction: 'left' | 'right') => void;
  threshold?: number; // Minimum distance for a swipe
  preventScroll?: boolean; // Prevent default scroll behavior
}

export const useSwipeCarousel = (options: SwipeCarouselOptions = {}) => {
  const { onSwipe, threshold = 50, preventScroll = true } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number>(0);
  const startY = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const startScrollLeft = useRef<number>(0);

  // Handle touch start
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!containerRef.current) return;

    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    startScrollLeft.current = containerRef.current.scrollLeft;
    isDragging.current = true;
  }, []);

  // Handle mouse down
  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!containerRef.current) return;

    startX.current = e.clientX;
    startY.current = e.clientY;
    startScrollLeft.current = containerRef.current.scrollLeft;
    isDragging.current = true;

    // Prevent text selection while dragging
    e.preventDefault();

    // Add cursor grabbing style
    containerRef.current.style.cursor = 'grabbing';
    containerRef.current.style.userSelect = 'none';
  }, []);

  // Handle touch/mouse move
  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging.current || !containerRef.current) return;

      const deltaX = startX.current - clientX;
      const deltaY = startY.current - clientY;

      // If vertical movement is greater than horizontal, don't interfere with vertical scrolling
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        return;
      }

      // Prevent default scroll behavior if specified
      if (preventScroll) {
        // This will be handled by the event listener
      }

      // Update scroll position
      containerRef.current.scrollLeft = startScrollLeft.current + deltaX;
    },
    [preventScroll],
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging.current) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(startX.current - touch.clientX);
      const deltaY = Math.abs(startY.current - touch.clientY);

      // If horizontal movement is greater, prevent vertical scrolling
      if (deltaX > deltaY && preventScroll) {
        e.preventDefault();
      }

      handleMove(touch.clientX, touch.clientY);
    },
    [handleMove, preventScroll],
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    },
    [handleMove],
  );

  // Handle touch/mouse end
  const handleEnd = useCallback(
    (clientX: number) => {
      if (!isDragging.current || !containerRef.current) return;

      const deltaX = startX.current - clientX;

      // Reset cursor style
      containerRef.current.style.cursor = 'grab';
      containerRef.current.style.userSelect = '';

      // Determine swipe direction if threshold is met
      if (Math.abs(deltaX) > threshold && onSwipe) {
        if (deltaX > 0) {
          onSwipe('right'); // Swiped left, go to next
        } else {
          onSwipe('left'); // Swiped right, go to previous
        }
      }

      isDragging.current = false;
    },
    [threshold, onSwipe],
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (e.changedTouches.length > 0) {
        handleEnd(e.changedTouches[0].clientX);
      }
    },
    [handleEnd],
  );

  // Handle mouse up
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      handleEnd(e.clientX);
    },
    [handleEnd],
  );

  // Attach event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set initial cursor style
    container.style.cursor = 'grab';

    // Touch events (passive: false to allow preventDefault)
    container.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Mouse events
    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent context menu on long press
    container.addEventListener('contextmenu', (e) => e.preventDefault());

    // Cleanup
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('contextmenu', (e) => e.preventDefault());
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  // Utility functions for programmatic scrolling
  const scrollToIndex = useCallback((index: number) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const itemWidth = container.scrollWidth / container.children.length;

    container.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth',
    });
  }, []);

  const scrollNext = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    // For mobile (< 640px), use full viewport width minus padding
    // For desktop, use 85% width items
    const isMobile = window.innerWidth < 640;
    const itemWidth = isMobile
      ? container.offsetWidth - 32 // Full width minus padding for mobile
      : container.offsetWidth * 0.85; // 85% width for desktop

    container.scrollBy({
      left: itemWidth,
      behavior: 'smooth',
    });
  }, []);

  const scrollPrev = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    // For mobile (< 640px), use full viewport width minus padding
    // For desktop, use 85% width items
    const isMobile = window.innerWidth < 640;
    const itemWidth = isMobile
      ? container.offsetWidth - 32 // Full width minus padding for mobile
      : container.offsetWidth * 0.85; // 85% width for desktop

    container.scrollBy({
      left: -itemWidth,
      behavior: 'smooth',
    });
  }, []);

  return {
    containerRef,
    scrollToIndex,
    scrollNext,
    scrollPrev,
    isDragging: isDragging.current,
  };
};
